"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { CFTC_FUTURES_DISCLOSURE_FULL } from "@/lib/disclosures";

interface FirstTradeDisclosureModalProps {
  open: boolean;
  onAcknowledge: () => void;
  onClose: () => void;
}

/**
 * One-time pre-trade acknowledgement of the full CFTC Rule 1.55(b) risk
 * disclosure statement (SFWL-007). Presentational only — the caller decides
 * when to show it and what to do with the acknowledgement (see
 * `useCftcAcknowledgement` below).
 *
 * Escape closes WITHOUT acknowledging — the caller is responsible for
 * discarding any pending action (e.g. an order that hasn't been submitted).
 *
 * Consumers (SFWL-017 unified these behind `useCftcGate`, see
 * `app/hooks/useCftcGate.ts`, which wires `modalProps` straight into this
 * component's props):
 *   - `PaperTradingPanel` (SFWL-007, first opening paper-trade order) —
 *     migrated onto `useCftcGate`.
 *   - `AllocationModal` (SFWL-017, strategy allocation submit).
 *   - `ApplyConfirmationModal` (SFWL-017, portfolio-builder apply submit).
 */
export function FirstTradeDisclosureModal({
  open,
  onAcknowledge,
  onClose,
}: FirstTradeDisclosureModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!mounted || !open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cftc-disclosure-title"
        className="relative z-10 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
      >
        <h2
          id="cftc-disclosure-title"
          className="mb-4 text-lg font-semibold text-slate-900"
        >
          Futures Risk Disclosure Statement
        </h2>
        <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-700">
          {CFTC_FUTURES_DISCLOSURE_FULL}
        </pre>
        <div className="mt-6 flex justify-end">
          {/*
            No existing modal in this repo manages focus (checked
            ClosePositionModal, TpSlModal, WalletModal, AuthModal,
            AllocationModal) — no full focus trap, per project convention
            (future polish if that's ever needed). This is the minimal fix:
            move focus into the dialog so Enter/Tab don't fall through to
            the form behind the overlay. Plain `autoFocus` (rather than a
            ref + effect) works cleanly here because this button's DOM node
            is only ever created fresh exactly when the modal opens — the
            component returns null while `!mounted || !open`, so there's no
            stale-node edge case to guard against.
          */}
          <button
            type="button"
            autoFocus
            onClick={onAcknowledge}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
          >
            I understand the risks
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Tracks whether `${tenantId}:${userId}` has acknowledged the CFTC futures
 * risk disclosure, backed by localStorage under the key
 * `${tenantId}:${userId}:cftc-ack` (value: the string "true").
 *
 * Reads localStorage directly during render (no setState-in-effect): the
 * persisted value is derived via `useMemo` keyed on the storage key and an
 * internal version counter, so a fresh instance for the same
 * tenantId/userId always reflects the latest persisted value, and a
 * different tenantId or userId is independent.
 *
 * Storage access is guarded end to end: in storage-denied environments
 * (browser "block all cookies" settings, sandboxed webviews) both
 * `getItem` and `setItem` can throw `SecurityError` — unguarded, that would
 * either crash render (this hook is called unconditionally by
 * `PaperTradingPanel`, and there's no route-level ErrorBoundary) or abort
 * `acknowledge()` before the caller can clear its pending state, wedging
 * the modal open forever. `sessionAckedKey` is the fallback: it records the
 * current storage key before the guarded persist attempt, so a denied/failed
 * write still lets the user proceed for this tenant+user during the current
 * session — it just doesn't survive reload, which is an acceptable
 * degradation next to a white-screened Trade route or a permanently stuck
 * modal.
 */
export function useCftcAcknowledgement(tenantId: string, userId: string) {
  const key = `${tenantId}:${userId}:cftc-ack`;
  const [version, setVersion] = useState(0);
  const [sessionAckedKey, setSessionAckedKey] = useState<string | null>(null);

  const persistedAcked = useMemo(() => {
    // `version` has no bearing on the computed key — referencing it here
    // just forces this memo to re-read localStorage after `acknowledge()`
    // bumps it, without resorting to setState-in-effect.
    void version;
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(key) === "true";
    } catch {
      // Storage access denied — treat as "not yet acknowledged" via
      // persistence; `sessionAcked` (below) still covers this session.
      return false;
    }
  }, [key, version]);

  const acknowledge = useCallback(() => {
    // Set the in-session fallback FIRST — a throwing setItem below must
    // never prevent the caller (PaperTradingPanel's
    // handleAcknowledgeDisclosure) from clearing its pending order and
    // closing the modal.
    setSessionAckedKey(key);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(key, "true");
      } catch {
        // Persistence unavailable — swallow. The ack degrades to
        // per-visit (sessionAcked) instead of surviving reload.
      }
    }
    setVersion((v) => v + 1);
  }, [key]);

  return { acknowledged: sessionAckedKey === key || persistedAcked, acknowledge };
}
